import React, { useState, useRef, useEffect } from 'react';
import { keyRoot, normalizeKey } from '../utils/normalize';

export default function KeyInput({
  value,
  onChange,
  onBlur,
  suggestions = [],
  inputStyle,
  textStyle,
  placeholder = 'Key',
  selectedPrefix = ''
}) {
  const [filtered, setFiltered] = useState([]);
  const [show, setShow] = useState(false);
  const [activated, setActivated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const ref = useRef();
  const latestValueRef = useRef(value);
  useEffect(() => { latestValueRef.current = value; }, [value]);

  const normalizeForMatch = str =>
    (str || '').toLowerCase().replace(/\s+/g, '');

  const filterSuggestions = (input) => {
    const inputNorm = normalizeForMatch(input);
    const inputRoot = keyRoot(input).toUpperCase(); // Normalize root to uppercase

    return suggestions.filter(s => {
      const sRoot = keyRoot(s).toUpperCase();
      return sRoot === inputRoot;
    });
  };

  const handleInputChange = (e) => {
    latestValueRef.current = e.target.value;
    onChange(e);
  };

  const hasPrefix = Boolean(selectedPrefix);
  const displayValue = hasPrefix && !isEditing
    ? (value ? `${selectedPrefix}: ${value}` : '')
    : value;
  const effectivePlaceholder = hasPrefix ? `${selectedPrefix}: ${placeholder}` : placeholder;
  const displayWeight = hasPrefix && !isEditing && value ? 700 : undefined;

  useEffect(() => {
    if (!activated) return;

    const normInput = normalizeForMatch(value);
    const rootMatch = normInput.match(/^(c#|cb|c|d#|db|d|e#|eb|e|f#|fb|f|g#|gb|g|a#|ab|a|b#|bb|b)/i);
    if (!rootMatch) {
      setFiltered([]);
      setShow(false);
      return;
    }

    const matches = suggestions.filter(s =>
      normalizeForMatch(s).startsWith(normInput)
    );

    setFiltered(matches);
    setShow(matches.length > 0);
  }, [value, suggestions, activated]);

  return (
<div style={{ position: 'relative', minWidth: 0 }}>

  <input
    ref={ref}
    className="search-input"
    name="key"
    placeholder={effectivePlaceholder}
    autoComplete="off"
    value={displayValue}
    style={{
      ...inputStyle,
      width: '100%',
      boxSizing: 'border-box',
      fontWeight: displayWeight,
      minHeight: 'var(--ui-select-height, 28px)',
      fontSize: 'var(--ui-select-font, var(--ui-compact-font))',
      paddingLeft: 'var(--ui-select-pad-x, 10px)',
      paddingRight: 'var(--ui-select-pad-x, 10px)'
    }}
    onChange={handleInputChange}
    onFocus={() => {
      setIsEditing(true);
      setActivated(true);
      requestAnimationFrame(() => {
        try { ref.current?.select(); } catch {}
      });
      const val = value || '';
      const normInput = normalizeForMatch(val);
      const rootMatch = normInput.match(/^(c#|cb|c|d#|db|d|e#|eb|e|f#|fb|f|g#|gb|g|a#|ab|a|b#|bb|b)/i);

      if (!rootMatch) {
        setFiltered([]);
        setShow(false);
        return;
      }

      const matches = suggestions.filter(s =>
        normalizeForMatch(s).startsWith(normInput)
      );

      setFiltered(matches);
      setTimeout(() => setShow(matches.length > 0), 0);
    }}
    onBlur={() => {
      setTimeout(() => {
        setShow(false);
        if (typeof onBlur === 'function') onBlur(latestValueRef.current);
      }, 150);
      setIsEditing(false);
    }}
  />




      {show && (
        <ul style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          right: 0,
          background: 'color-mix(in srgb, var(--bg-card) 92%, #0b2238 8%)',
          border: '1px solid color-mix(in srgb, var(--text-link, #228be6) 28%, var(--card-border) 72%)',
          borderRadius: '12px',
          boxShadow: '0 10px 24px rgba(0,0,0,0.28)',
          maxHeight: '220px',
          overflowY: 'auto',
          margin: 0,
          padding: '4px',
          listStyle: 'none',
          zIndex: 1400,
        }}>
          {filtered.map((s, i) => (
            <li
              key={i}
              onMouseDown={() => {
                latestValueRef.current = s;
                if (s !== value) {
                  onChange({ target: { name: 'key', value: s } });
                }
                setShow(false);
                setFiltered([]);
                setTimeout(() => ref.current?.blur(), 50);
              }}
              style={{
                borderRadius: '8px',
                padding: 'var(--ui-select-option-pad-y, 6px) var(--ui-select-option-pad-x, 11px)',
                cursor: 'pointer',
                fontSize: 'var(--ui-select-font, var(--ui-compact-font))',
                lineHeight: 1.2,
                color: 'var(--text-primary)',
                background: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'color-mix(in srgb, var(--text-link, #228be6) 16%, transparent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
